/**
 * A class representing the Purchase Order Sending Options.
 */
export class POSendingOptions {
    /**
     * Create a PO Sending Options.
     * @param {boolean} requester - The requester value.
     * @param {boolean} spender - The spender value.
     * @param {boolean} other - The other value.
     * @param {string} otherAddress - The otherAddress value.
     * @param {boolean} supplier - The supplier value.
     * @param {string[]} supplierAddresses - The supplierAddresses value.
     * @param {boolean} p2pSupplier - Whether the supplier addresses are taken from P2P output table.
     * @param {boolean} pslSupplier - Whether the supplier addresses are taken from Preferred Supplier Value.
     * @param {boolean} vmdSupplier - Whether the supplier addresses are taken from Vendor Master Data.
     * @param {boolean} hidePrice - The hidePrice value.
     * @param {string} coversheet - The coversheet value.
     * @param {boolean} p2pOther - Whether the supplier addresses are taken from P2P output table for other field.
     */
    constructor(
        requester,
        spender,
        other,
        otherAddress,
        supplier,
        supplierAddresses,
        p2pSupplier,
        pslSupplier,
        vmdSupplier,
        hidePrice,
        coversheet,
        p2pOther,
    ) {
        this._requester = requester;
        this._spender = spender;
        this._other = other;
        this._otherAddress = otherAddress;
        this._supplier = supplier;
        this._supplierAddresses = supplierAddresses;
        this._p2pSupplier = p2pSupplier;
        this._pslSupplier = pslSupplier;
        this._vmdSupplier = vmdSupplier;
        this._hidePrice = hidePrice;
        this._coversheet = coversheet;
        this._p2pOther = p2pOther;
    }

    /**
     * Get an JS object containing all the information about the PO Sending options.
     * @return {{requester: boolean, other: boolean, supplierAddresses: string[], pslSupplier: boolean, spender: boolean, otherAddress: string, supplier: boolean, p2pSupplier: boolean, vmdSupplier: boolean, coversheet: string, hidePrice: boolean, buyer: boolean}}
     */
    getPOSendingOptions() {
        return {
            requester: this._requester,
            spender: this._spender,
            buyer: this._buyer,
            other: this._other,
            otherAddress: this._otherAddress,
            supplier: this._supplier,
            supplierAddresses: this._supplierAddresses,
            p2pSupplier: this._p2pSupplier,
            pslSupplier: this._pslSupplier,
            vmdSupplier: this._vmdSupplier,
            hidePrice: this._hidePrice,
            coversheet: this._coversheet,
            p2pOther: this._p2pOther,
        };
    }

    /**
     * Get the requester value.
     * @return {boolean} The requester value.
     */
    get requester() {
        return this._requester;
    }

    /**
     * Set the requester value.
     * @param {boolean} value to set
     */
    set requester(value) {
        this._requester = value;
    }

    /**
     * Get the spender value.
     * @return {boolean} The spender value.
     */
    get spender() {
        return this._spender;
    }

    /**
     * Set the spender value.
     * @param {boolean} value to set
     */
    set spender(value) {
        this._spender = value;
    }

    /**
     * Get the buyer value.
     * @return {boolean} The buyer value.
     */
    get buyer() {
        return this._buyer;
    }

    /**
     * Set the buyer value.
     * @param {boolean} value to set
     */
    set buyer(value) {
        this._buyer = value;
    }

    /**
     * Get the other value.
     * @return {boolean} The other value.
     */
    get other() {
        return this._other;
    }

    /**
     * Set the other value.
     * @param {boolean} value to set
     */
    set other(value) {
        this._other = value;
    }

    /**
     * Get the otherAddress value.
     * @return {string} The otherAddress value.
     */
    get otherAddress() {
        return this._otherAddress;
    }

    /**
     * Set the otherAddress value.
     * @param {string} value to set
     */
    set otherAddress(value) {
        this._otherAddress = value;
    }

    /**
     * Get the supplier value.
     * @return {boolean} The supplier value.
     */
    get supplier() {
        return this._supplier;
    }

    /**
     * Set the supplier value.
     * @param {boolean} value to set
     */
    set supplier(value) {
        this._supplier = value;
    }

    /**
     * Get the supplierAddresses value.
     * @return {string[]} The supplierAddresses value.
     */
    get supplierAddresses() {
        return this._supplierAddresses;
    }

    /**
     * Set the supplierAddresses value.
     * @param {string[]} value to set
     */
    set supplierAddresses(value) {
        this._supplierAddresses = value;
    }

    /**
     * Get the P2P supplier value.
     * @return {boolean} The P2P supplier value.
     */
    get p2pSupplier() {
        return this._p2pSupplier;
    }

    /**
     * Set the P2P supplier value.
     * @param {boolean} value to set
     */
    set p2pSupplier(value) {
        this._p2pSupplier = value;
    }

    /**
     * Get the PSL supplier value.
     * @return {boolean} The PSL supplier value.
     */
    get pslSupplier() {
        return this._pslSupplier;
    }

    /**
     * Set the PSL supplier value.
     * @param {boolean} value to set
     */
    set pslSupplier(value) {
        this._pslSupplier = value;
    }

    /**
     * Get the VMD supplier value.
     * @return {boolean} The VMD supplier value.
     */
    get vmdSupplier() {
        return this._vmdSupplier;
    }

    /**
     * Set the VMD supplier value.
     * @param {boolean} value to set
     */
    set vmdSupplier(value) {
        this._vmdSupplier = value;
    }

    /**
     * Get the hidePrice value.
     * @return {boolean} The hidePrice value.
     */
    get hidePrice() {
        return this._hidePrice;
    }

    /**
     * Set the hidePrice value.
     * @param {boolean} value to set
     */
    set hidePrice(value) {
        this._hidePrice = value;
    }

    /**
     * Get the coversheet value.
     * @return {string} The coversheet value.
     */
    get coversheet() {
        return this._coversheet;
    }

    /**
     * Set the coversheet value.
     * @param {string} value to set
     */
    set coversheet(value) {
        this._coversheet = value;
    }

    /**
     * Get the P2P other value.
     * @return {boolean} The P2P other value.
     */
    get p2pOther() {
        return this._p2pOther;
    }

    /**
     * Set the P2P other value.
     * @param {boolean} value to set
     */
    set p2pOther(value) {
        this._p2pOther = value;
    }
}
